import { AlertMessage, NavigationPage } from '@/app/components'
import { Button, Icon, LoadingAnimation, Text } from '@/components'
import { useEffect, useState } from 'react'
import { scopedFleetAuthenticationCollection } from '@/reducers/teslaFleetAuthentication'
import { useParams } from 'react-router-dom'
import { DeviceAgnosticFleetAuthentication } from '@/types/teslaFleetAuthentication'
import useApiDispatch from '@/hooks/useApiDispatch/useApiDispatch'
import { energySystemCollection } from '@/reducers/energySystems'
import { EnergySystem } from '@/types/energySystem'
import { useNavigation } from '@/app/hooks'
import { useAppDispatch } from '@/hooks'
import { logException } from '@/logging/sentry'

export function useViewModel() {
  const navigation = useNavigation()
  const { authenticationId } = useParams<{
    authenticationId: string
  }>()
  const { apiDispatch } = useApiDispatch()
  const dispatch = useAppDispatch()
  const [error, setError] = useState<boolean>(false)
  const { createAction: refreshPermissionsAction } =
    scopedFleetAuthenticationCollection.useCreate({
      path: `tesla-fleet-authentication/${authenticationId}/refresh`,
    })
  const { createAction: initializeEnergySystemsAction } =
    energySystemCollection.useCreate({
      path: 'energy-systems/tesla/initialize',
    })

  const refreshPermissionsAndInitializeEnergySystems = async (
    authenticationId: string,
  ) => {
    /**
     * 1. Refresh the permissions on the authentication
     * 2. Initiailize the energy systems
     * 3. Redirect the user to the permissions verification page
     */
    // 1. Refresh the permissions on the authentication
    const res = await apiDispatch<DeviceAgnosticFleetAuthentication>(
      refreshPermissionsAction(),
    )
    if (!res.ok) {
      logException(new Error(JSON.stringify(res.error)), { logToConsole: true })
      setError(true)
      return
    }

    // 2. Initialize the energy systems
    const initializeEnergySystemsRes = await apiDispatch<EnergySystem[]>(
      initializeEnergySystemsAction({
        id: res.data.id,
      }),
    )
    dispatch(energySystemCollection.actions.invalidate())
    if (!initializeEnergySystemsRes.ok) {
      // if the user doesn't have sufficient permissions, redirect them to the no system information page
      if (initializeEnergySystemsRes.error.status === 403) {
        navigation.push(
          `/tesla-authentication/energy-system/no-system-information/${authenticationId}`,
        )
        return
      }
      logException(
        new Error(JSON.stringify(initializeEnergySystemsRes.error)),
        {
          logToConsole: true,
        },
      )
      setError(true)
      return
    }

    if (initializeEnergySystemsRes.data.length === 0) {
      // todo we should check the scopes and have a separate error page saying
      // they gave us permissions but don't have any energy systems
      navigation.push(
        `/tesla-authentication/energy-system/no-system-information/${authenticationId}`,
      )
      return
    }

    // 3. Redirect the user to the permissions verification page
    navigation.push(
      `/tesla-authentication/energy-system/verify-permissions/${authenticationId}`,
    )
  }

  useEffect(() => {
    if (authenticationId) {
      refreshPermissionsAndInitializeEnergySystems(authenticationId)
    }
  }, [authenticationId])

  return { error }
}

const ErrorContent = () => {
  const navigation = useNavigation()
  return (
    <div className="flex flex-col items-center justify-center flex-grow w-full">
      <div className="flex flex-col items-center mb-8">
        <div className="flex justify-center pb-6">
          <Icon name="Frown" />
        </div>
        <Text variant="h3" className=" max-w-md">
          There was an error connecting to your Tesla account. Please try again
          later, or contact support if the problem persists.
        </Text>
      </div>
      <Button
        id="retry-tesla-fleet-auth-redirect-energy-system"
        variant="primary"
        onClick={() => {
          navigation.goHome()
        }}
      >
        Retry
      </Button>
    </div>
  )
}

const LoadingContent = () => {
  return (
    <div className="flex flex-col items-center justify-center flex-grow w-full">
      <div className="flex flex-col items-center">
        <div className="flex justify-center pb-6">
          <LoadingAnimation type="falling" />
        </div>
        <Text variant="h3">
          Hold tight... we're reconnecting your Tesla account
        </Text>
        <div className="w-full py-6 mt-4">
          <AlertMessage variant="info" style={{ width: '100%' }}>
            This may take a moment
          </AlertMessage>
        </div>
      </div>
    </div>
  )
}

export default function TeslaFleetAuthRedirectEnergySystem() {
  const { error } = useViewModel()

  return (
    <NavigationPage id="tesla-fleet-auth-redirect-energy-system">
      <NavigationPage.SingleCardContent>
        {error ? <ErrorContent /> : <LoadingContent />}
      </NavigationPage.SingleCardContent>
    </NavigationPage>
  )
}
